<template>
  <v-card class="mb-4 pa-2 card" color="surface" variant="elevated" elevation="4" density="compact">
    <div class="d-flex align-center px-2 py-1">
      <v-card-title class="max-85 pr-0 py-1">{{ name }}</v-card-title>
      <v-spacer />
      <v-btn
        color="primary"
        class="ml-auto"
        :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        @click="toggleShow(!show)"
        density="compact"
      />
    </div>
    <div v-if="show" class="mx-0 my-2 pa-0">
      <v-divider />
    </div>
    <v-form class="ma-0 pa-0" @submit.prevent density="compact">
      <v-expand-transition>
        <v-list class="ma-0 pa-0" v-show="show" density="compact">
          <!-- Tool Description -->
          <v-list-item
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0 tool-execution-item' : 'ma-0 pa-0 tool-execution-item'"
            density="compact"
          >
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300" text="Description of what this tool does.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-textarea v-model="description" readonly label="Description" class="pt-2"
              variant="outlined" density="compact" auto-grow rows="2" row-height="20">
            </v-textarea>
          </v-list-item>

          <!-- Required Parameters -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact" v-if="requiredParams.length > 0">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300" text="Parameters that must be provided when using this tool.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-expansion-panels>
              <v-expansion-panel title="Required Parameters" density="compact">
                <v-expansion-panel-text>
                  <div v-for="param in requiredParams" :key="param.name" class="mb-2">
                    <div class="d-flex align-center">
                      <strong class="mr-2">{{ param.name }}</strong>
                      <v-chip size="x-small" class="mr-2 param-type-chip param-required-chip">Required</v-chip>
                      <span class="text-caption param-type-text">{{ param.type }}</span>
                    </div>
                    <p class="text-body-2 ml-2">{{ param.description }}</p>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-list-item>

          <!-- Optional Parameters -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact" v-if="optionalParams.length > 0">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300" text="Parameters that can optionally be provided when using this tool.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-expansion-panels>
              <v-expansion-panel title="Optional Parameters" density="compact">
                <v-expansion-panel-text>
                  <div v-for="param in optionalParams" :key="param.name" class="mb-2">
                    <div class="d-flex align-center">
                      <strong class="mr-2">{{ param.name }}</strong>
                      <v-chip size="x-small" class="mr-2 param-type-chip param-optional-chip">Optional</v-chip>
                      <span class="text-caption param-type-text">{{ param.type }}</span>
                    </div>
                    <p class="text-body-2 ml-2">{{ param.description }}</p>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-list-item>

          <!-- Usage Example -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact" v-if="example">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300" text="Example of how to use this tool.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning" class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-expansion-panels>
              <v-expansion-panel title="Usage Example" density="compact">
                <v-expansion-panel-text>
                  <pre class="code-block">{{ example }}</pre>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-list-item>

          <!-- Tool Execution -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-divider class="my-2" />

            <v-alert v-if="!serverEnabled" type="warning" density="compact" variant="tonal" class="mb-3">
              This server is disabled. Enable it to run this tool.
            </v-alert>

            <div
              v-for="param in parameters"
              :key="`tool-param-${toolId}-${param.name}`"
              :class="['param-input-row', { 'param-input-row-boolean': param.type === 'boolean' }]"
            >
              <v-switch
                v-if="param.type === 'boolean'"
                v-model="argInputs[param.name]"
                :label="`${param.name}${param.required ? ' *' : ''}`"
                color="primary"
                :hide-details="!fieldErrors[param.name]"
                density="compact"
                class="tool-boolean-switch"
                :error-messages="fieldErrors[param.name] ? [fieldErrors[param.name]] : []"
              />

              <v-text-field
                v-else-if="param.type === 'number'"
                v-model="argInputs[param.name]"
                type="number"
                :label="`${param.name}${param.required ? ' *' : ''}`"
                variant="outlined"
                density="compact"
                :error-messages="fieldErrors[param.name] ? [fieldErrors[param.name]] : []"
              />

              <v-textarea
                v-else-if="param.type === 'object' || param.type === 'array'"
                v-model="argInputs[param.name]"
                :label="`${param.name}${param.required ? ' *' : ''}`"
                variant="outlined"
                density="compact"
                auto-grow
                rows="2"
                :error-messages="fieldErrors[param.name] ? [fieldErrors[param.name]] : []"
              />

              <v-text-field
                v-else
                v-model="argInputs[param.name]"
                :label="`${param.name}${param.required ? ' *' : ''}`"
                variant="outlined"
                density="compact"
                :error-messages="fieldErrors[param.name] ? [fieldErrors[param.name]] : []"
              />

              <div
                v-if="param.description"
                :class="[
                  'param-description',
                  { 'param-description-warning': parameterDescriptionIsWarning(param.description) }
                ]"
              >
                <v-icon v-if="parameterDescriptionIsWarning(param.description)" size="x-small" color="warning" class="mr-1">mdi-alert-outline</v-icon>
                <span>{{ param.description }}</span>
                <span v-if="param.type === 'object' || param.type === 'array'" class="ml-1">(expects JSON {{ param.type }})</span>
              </div>
            </div>

            <div class="d-flex flex-wrap ga-2 mt-3">
              <v-btn color="primary" :loading="runLoading" :disabled="!serverEnabled || runLoading" @click="runTool">
                Run Tool
              </v-btn>
              <v-btn
                v-if="runResult !== null && runResult !== undefined"
                variant="outlined"
                color="secondary"
                @click="copyResultPreview"
              >
                Copy Preview
              </v-btn>
              <v-btn
                v-if="runResult !== null && runResult !== undefined"
                variant="outlined"
                color="secondary"
                @click="downloadResult"
              >
                Download Full Result
              </v-btn>
            </div>

            <v-alert v-if="hasFieldErrors" type="error" class="mt-2" density="compact" variant="tonal">
              Fix highlighted parameter errors before running this tool.
            </v-alert>

            <v-alert v-if="runError" type="error" class="mt-3" density="compact" variant="tonal">
              {{ runError }}
              <div v-if="showReconnectAction" class="mt-2">
                <v-btn size="small" color="primary" variant="outlined" @click="requestReconnect">
                  Reconnect OAuth
                </v-btn>
              </div>
            </v-alert>

            <div v-if="runResult !== null && runResult !== undefined" class="mt-3">
              <div class="text-caption mb-1">
                Response size: {{ responseSizeBytes }} bytes
              </div>
              <v-alert v-if="isLargeResponse" type="info" density="compact" variant="tonal" class="mb-2">
                Showing preview only. Download full response.
              </v-alert>
              <div class="response-block" v-if="parsedResponseJson.isJson">
                <vue-json-pretty :data="parsedResponseJson.data" :deep="3" theme="dark" />
              </div>
              <pre v-else class="code-block response-block">{{ responsePreview }}</pre>
            </div>
          </v-list-item>
        </v-list>
      </v-expand-transition>

    </v-form>
  </v-card>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'

interface ToolParameter {
  name: string
  type: string
  description: string
  required: boolean
  defaultValue?: unknown
}

type ToolInputValue = string | boolean | null

@Options({
  props: {
    toolId: String,
    serverId: String,
    activeToolId: String
  },
  emits: ['tool-selected', 'connect-oauth'],
  computed: {
    ...mapGetters([
      'mcpTools',
      'mcpServers',
      'mcpToolCallLoadingByKey',
      'mcpToolCallErrorByKey',
      'mcpToolCallResultByKey',
      'showGlobalTooltips'
    ])
  },
  watch: {
    toolId: {
      handler() {
        this.onToolContextChanged()
      },
      immediate: true
    },
    serverId: {
      handler() {
        this.onToolContextChanged()
      },
      immediate: true
    },
    activeToolId(newVal: string | null) {
      if (newVal !== this.toolId) {
        this.show = false
        this.resetLocalState()
        if (this.callKey) {
          this.$store.dispatch('clearToolCallState', this.callKey)
        }
      }
    }
  }
})
export default class McpToolCard extends mixins(AnalyticsMixin) {
  private show_: boolean = false
  public toolId!: string
  public serverId!: string
  public activeToolId!: string | null
  public mcpTools!: Record<string, Record<string, any>>
  public mcpServers!: Record<string, any>
  public mcpToolCallLoadingByKey!: Record<string, boolean>
  public mcpToolCallErrorByKey!: Record<string, { success: false; error: string; code?: string; reauthRequired?: boolean } | null>
  public mcpToolCallResultByKey!: Record<string, unknown>
  public showGlobalTooltips!: boolean

  public name: string = ''
  public description: string = ''
  public parameters: ToolParameter[] = []
  public example: string = ''
  public argInputs: Record<string, ToolInputValue> = {}
  public fieldErrors: Record<string, string> = {}
  public responsePreview: string = ''
  public responseSizeBytes = 0
  public isLargeResponse = false
  public lastCallKey: string | null = null

  private readonly MAX_INLINE_RESPONSE_BYTES = 200 * 1024

  created() {
    this.loadToolDataForServerAndTool()
    this.lastCallKey = this.callKey
  }

  private resetLocalState() {
    this.argInputs = {}
    this.fieldErrors = {}
    this.responsePreview = ''
    this.responseSizeBytes = 0
    this.isLargeResponse = false
  }

  private initialValueForParam(param: ToolParameter): ToolInputValue {
    if (param.type === 'boolean') {
      return this.toBooleanOrNull(param.defaultValue)
    }

    if (param.type === 'number') {
      return typeof param.defaultValue === 'number' ? String(param.defaultValue) : ''
    }

    if (param.type === 'object' || param.type === 'array') {
      if (param.defaultValue === undefined) {
        return ''
      }
      try {
        return JSON.stringify(param.defaultValue, null, 2)
      } catch (_error) {
        return ''
      }
    }

    if (typeof param.defaultValue === 'string') {
      return param.defaultValue
    }

    return ''
  }

  private toBooleanOrNull(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value
    if (value === 'true') return true
    if (value === 'false') return false
    return null
  }

  private loadToolDataForServerAndTool() {
    const tool = this.mcpTools?.[this.serverId]?.[this.toolId]
    if (!tool) {
      this.name = ''
      this.description = ''
      this.parameters = []
      this.example = ''
      return
    }

    this.name = tool.name || ''
    this.description = tool.description || ''
    this.parameters = Array.isArray(tool.parameters) ? tool.parameters : []
    this.example = tool.example || ''

    const nextInputs: Record<string, ToolInputValue> = {}
    this.parameters.forEach((param) => {
      nextInputs[param.name] = this.initialValueForParam(param)
    })
    this.argInputs = nextInputs

    if (this.callKey) {
      const existingResult = this.mcpToolCallResultByKey[this.callKey]
      if (existingResult !== undefined) {
        this.syncResponsePreview()
      }
    }
  }

  private onToolContextChanged() {
    if (this.lastCallKey) {
      this.$store.dispatch('clearToolCallState', this.lastCallKey)
    }
    this.resetLocalState()
    this.loadToolDataForServerAndTool()
    this.lastCallKey = this.callKey
  }

  toggleShow(shouldShow: boolean) {
    this.show = shouldShow
    if (shouldShow) {
      this.$emit('tool-selected', this.toolId)
      this.trackButtonClick('view_mcp_tool_details', 'expand', { tool_id: this.toolId, tool_name: this.name })
    }
  }

  get show(): boolean {
    return this.show_
  }

  set show(value: boolean) {
    this.show_ = value
  }

  get serverName(): string {
    return this.mcpServers?.[this.serverId]?.name || ''
  }

  get serverEnabled(): boolean {
    return this.mcpServers?.[this.serverId]?.enabled === true
  }

  get callKey(): string | null {
    if (!this.serverName || !this.name) return null
    return `${this.serverName}::${this.name}`
  }

  get runLoading(): boolean {
    return this.callKey ? this.mcpToolCallLoadingByKey[this.callKey] === true : false
  }

  get runFailure(): { success: false; error: string; code?: string; reauthRequired?: boolean } | null {
    return this.callKey ? this.mcpToolCallErrorByKey[this.callKey] || null : null
  }

  get runError(): string | null {
    return this.runFailure?.error || null
  }

  get showReconnectAction(): boolean {
    return this.runFailure?.code === 'reauth_required' || this.runFailure?.reauthRequired === true
  }

  get runResult(): unknown {
    return this.callKey ? this.mcpToolCallResultByKey[this.callKey] : null
  }

  /**
   * Attempt to parse stringified JSON values nested inside MCP content items.
   * MCP tools commonly return { content: [{ type: "text", text: "<json>" }] }.
   * This deep-parses those inner text strings so vue-json-pretty renders the full tree.
   */
  private deepParseMcpContent(data: unknown): unknown {
    if (!this.isRecord(data)) return data
    const content = data.content
    if (!Array.isArray(content)) return data

    let didParse = false
    const parsed = content.map((item: unknown) => {
      if (!this.isRecord(item)) return item
      if (item.type !== 'text' || typeof item.text !== 'string') return item
      try {
        const inner = JSON.parse(item.text)
        if (typeof inner === 'object' && inner !== null) {
          didParse = true
          return { ...item, text: inner }
        }
      } catch (_e) {
        // not JSON, keep original string
      }
      return item
    })

    return didParse ? { ...data, content: parsed } : data
  }

  get parsedResponseJson(): { isJson: boolean; data: unknown } {
    const result = this.runResult
    if (result === null || result === undefined) {
      return { isJson: false, data: null }
    }
    // If result is already an object/array (not a string), deep-parse MCP content and use it
    if (typeof result === 'object') {
      return { isJson: true, data: this.deepParseMcpContent(result) }
    }
    // If it's a string, attempt to parse it
    if (typeof result === 'string') {
      try {
        const parsed = JSON.parse(result)
        if (typeof parsed === 'object' && parsed !== null) {
          return { isJson: true, data: this.deepParseMcpContent(parsed) }
        }
      } catch (_e) {
        // not JSON
      }
    }
    return { isJson: false, data: null }
  }

  get requiredParams(): ToolParameter[] {
    return this.parameters.filter(param => param.required)
  }

  get optionalParams(): ToolParameter[] {
    return this.parameters.filter(param => !param.required)
  }

  get hasFieldErrors(): boolean {
    return Object.keys(this.fieldErrors).length > 0
  }

  private parameterDescriptionIsWarning(description: string): boolean {
    return /warning/i.test(description)
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
  }

  private parseArgs(): { ok: boolean; args: Record<string, unknown> } {
    const errors: Record<string, string> = {}
    const args: Record<string, unknown> = {}

    for (const param of this.parameters) {
      const raw = this.argInputs[param.name]
      const hasDefault = Object.prototype.hasOwnProperty.call(param, 'defaultValue')
      const candidate = (raw === null || raw === '') && hasDefault ? param.defaultValue : raw
      const isEmpty = candidate === null || candidate === '' || candidate === undefined

      if (param.required && isEmpty) {
        errors[param.name] = 'Required parameter'
        continue
      }

      if (isEmpty) {
        continue
      }

      if (param.type === 'number') {
        const parsed = typeof candidate === 'number' ? candidate : Number(candidate)
        if (Number.isNaN(parsed)) {
          errors[param.name] = 'Must be a valid number'
        } else {
          args[param.name] = parsed
        }
        continue
      }

      if (param.type === 'boolean') {
        if (typeof candidate === 'boolean') {
          args[param.name] = candidate
          continue
        }
        if (candidate === 'true' || candidate === 'false') {
          args[param.name] = candidate === 'true'
        } else {
          errors[param.name] = 'Must be true or false'
        }
        continue
      }

      if (param.type === 'object') {
        if (this.isRecord(candidate)) {
          args[param.name] = candidate
          continue
        }
        try {
          const parsed = JSON.parse(String(candidate))
          if (!this.isRecord(parsed)) {
            errors[param.name] = 'Must be a JSON object'
          } else {
            args[param.name] = parsed
          }
        } catch (_error) {
          errors[param.name] = 'Invalid JSON object'
        }
        continue
      }

      if (param.type === 'array') {
        if (Array.isArray(candidate)) {
          args[param.name] = candidate
          continue
        }
        try {
          const parsed = JSON.parse(String(candidate))
          if (!Array.isArray(parsed)) {
            errors[param.name] = 'Must be a JSON array'
          } else {
            args[param.name] = parsed
          }
        } catch (_error) {
          errors[param.name] = 'Invalid JSON array'
        }
        continue
      }

      args[param.name] = String(candidate)
    }

    this.fieldErrors = errors
    return { ok: Object.keys(errors).length === 0, args }
  }

  async runTool() {
    if (!this.serverEnabled) {
      this.$store.dispatch('updateSnackbar', { text: 'Server is disabled for this tool', color: 'error' }, { root: true })
      return
    }

    if (this.runLoading) {
      return
    }

    const { ok, args } = this.parseArgs()
    if (!ok) {
      this.$store.dispatch('updateSnackbar', { text: 'Fix parameter errors before running', color: 'error' }, { root: true })
      return
    }

    await this.$store.dispatch('callMcpTool', {
      serverId: this.serverId,
      toolId: this.toolId,
      args
    })

    this.syncResponsePreview()
  }

  requestReconnect() {
    this.$emit('connect-oauth', this.serverName)
  }

  private stringifyResult(result: unknown): string {
    return typeof result === 'string' ? result : JSON.stringify(result ?? null, null, 2)
  }

  syncResponsePreview() {
    const serialized = this.stringifyResult(this.runResult)
    this.responseSizeBytes = new Blob([serialized]).size
    this.isLargeResponse = this.responseSizeBytes > this.MAX_INLINE_RESPONSE_BYTES
    this.responsePreview = this.isLargeResponse ? serialized.slice(0, 4000) : serialized
  }

  private detectContentTypeFromResult(result: unknown): string | null {
    if (!this.isRecord(result)) return null
    const direct = result.contentType
    const mime = result.mimeType
    if (typeof direct === 'string' && direct.trim() !== '') return direct
    if (typeof mime === 'string' && mime.trim() !== '') return mime
    return null
  }

  private detectBase64Payload(result: unknown): string | null {
    if (!this.isRecord(result)) return null
    if (typeof result.base64 === 'string') return result.base64
    if (typeof result.base64Data === 'string') return result.base64Data
    return null
  }

  private extensionForMime(mime: string): string {
    const normalized = mime.toLowerCase()
    if (normalized.includes('json')) return 'json'
    if (normalized.includes('text/plain')) return 'txt'
    if (normalized.includes('text/csv')) return 'csv'
    if (normalized.includes('pdf')) return 'pdf'
    if (normalized.includes('png')) return 'png'
    if (normalized.includes('jpeg') || normalized.includes('jpg')) return 'jpg'
    return 'bin'
  }

  private makeDownloadBlob(result: unknown): { blob: Blob; extension: string } {
    if (typeof result === 'string') {
      return {
        blob: new Blob([result], { type: 'text/plain;charset=utf-8' }),
        extension: 'txt'
      }
    }

    const inferredMime = this.detectContentTypeFromResult(result)
    const inferredBase64 = this.detectBase64Payload(result)

    if (inferredMime && inferredBase64) {
      try {
        const binary = atob(inferredBase64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        return {
          blob: new Blob([bytes], { type: inferredMime }),
          extension: this.extensionForMime(inferredMime)
        }
      } catch (_error) {
        // fallback below
      }
    }

    const json = JSON.stringify(result ?? null, null, 2)
    return {
      blob: new Blob([json], { type: 'application/json;charset=utf-8' }),
      extension: 'json'
    }
  }

  downloadResult() {
    const { blob, extension } = this.makeDownloadBlob(this.runResult)
    const serverPart = (this.serverName || 'server').replace(/[^a-zA-Z0-9_-]/g, '_')
    const methodPart = (this.name || 'tool').replace(/[^a-zA-Z0-9_-]/g, '_')
    const fileName = `${serverPart}-${methodPart}-${Date.now()}.${extension}`

    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  async copyResultPreview() {
    await navigator.clipboard.writeText(this.responsePreview)
    this.$store.dispatch('updateSnackbar', { text: 'Response preview copied' }, { root: true })
  }
}
</script>

<style scoped lang="scss">
.max-85 {
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-tooltip-icon {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.code-block {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 8px;
  border-radius: 4px;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

.response-block {
  max-height: 320px;
  overflow: auto;
}

.tool-execution-item {
  overflow: visible !important;
}

.tool-boolean-switch {
  padding-inline-start: 8px;
}

.tool-boolean-switch :deep(.v-selection-control) {
  margin-inline-start: 0 !important;
}

.param-input-row {
  margin-bottom: 12px;
}

.param-description {
  margin-top: 2px;
  margin-left: 12px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.72);
  white-space: pre-wrap;
  word-break: break-word;
}

.param-input-row-boolean .param-description {
  margin-left: 40px;
}

.param-description-warning {
  margin-left: 0;
  padding: 8px 10px;
  border-left: 2px solid rgba(var(--v-theme-warning), 0.75);
  border-radius: 4px;
  background: rgba(var(--v-theme-warning), 0.12);
  color: rgb(var(--v-theme-on-surface));
}

.param-input-row-boolean .param-description-warning {
  margin-left: 40px;
}

.param-type-chip {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  font-weight: 600;
}

.param-required-chip {
  background: rgba(var(--v-theme-primary), 0.2);
  color: rgb(var(--v-theme-on-surface));
}

.param-optional-chip {
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgb(var(--v-theme-on-surface));
}

.param-type-text {
  color: rgba(var(--v-theme-on-surface), 0.65);
}
</style>
